import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { createElement } from "react";
import * as authService from "@/services/auth";
import * as platformApi from "@/services/platform-admin-api";

// ── Types ─────────────────────────────────────────────────────────────────

interface AuthUser {
  id: string;
  email: string;
  role: string;
  tenant_id: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isPlatformAdmin: boolean;
  isImpersonating: boolean;
  impersonationTenantName: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setAuthUser: (user: AuthUser) => void;
  platformAdminLogin: (email: string, password: string) => Promise<void>;
  platformAdminLogout: () => void;
  startImpersonation: (
    tenantId: string,
    reason: platformApi.ImpersonationReason,
  ) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

// ── JWT helpers ───────────────────────────────────────────────────────────

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function checkImpersonation(): boolean {
  const token = localStorage.getItem("access_token");
  if (!token) return false;
  const payload = parseJwtPayload(token);
  return !!payload?.act;
}

// localStorage key holding the tenant display name for the banner. The token
// is the source of truth for the impersonation itself; tenantName is not a JWT
// claim, so we persist it separately purely for display.
const PA_IMPERSONATION_KEY = "pa_impersonation";

// Build a synthetic tenant AuthUser from the impersonation token's act claim,
// so ProtectedRoute (isAuthenticated = !!user) lets the tenant-admin routes
// render while acting under the impersonation token.
function impersonationUserFromToken(token: string): AuthUser | null {
  const payload = parseJwtPayload(token);
  const act = payload?.act as
    | { sub?: string; tenantId?: string; role?: string }
    | undefined;
  if (!act?.sub || !act.tenantId || !act.role) return null;
  return {
    id: act.sub,
    tenant_id: act.tenantId,
    role: act.role,
    email:
      typeof payload?.email === "string"
        ? payload.email
        : "impersonation@platform.internal",
  };
}

// sessionId lives in the token (single source of truth), not in localStorage.
function impersonationSessionIdFromToken(token: string): string | null {
  const payload = parseJwtPayload(token);
  const sid = payload?.impersonationSessionId;
  return typeof sid === "string" ? sid : null;
}

function readImpersonationTenantName(): string | null {
  if (!checkImpersonation()) return null; // token = source of truth
  try {
    const raw = localStorage.getItem(PA_IMPERSONATION_KEY);
    const parsed = raw ? (JSON.parse(raw) as { tenantName?: string }) : null;
    return parsed?.tenantName ?? "Unbekannter Tenant";
  } catch {
    return "Unbekannter Tenant";
  }
}

// ── Context ───────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Tenant-admin state
  const [user, setUser] = useState<AuthUser | null>(authService.getCurrentUser);

  // Platform-admin state
  const [platformUser, setPlatformUser] =
    useState<platformApi.PlatformAdminUser | null>(
      platformApi.getPlatformAdminUser,
    );

  // Impersonation display state (reactive — drives the banner). Initialized
  // from the token + persisted tenantName so an active session survives reload.
  const [impersonationTenantName, setImpersonationTenantName] = useState<
    string | null
  >(readImpersonationTenantName);

  // Derived state
  const isPlatformAdmin = platformUser?.role === "SUPER_ADMIN";
  const isImpersonating = impersonationTenantName !== null;

  // ── Tenant login/logout (unchanged logic) ───────────────────────────────

  const login = useCallback(async (email: string, password: string) => {
    const loggedInUser = await authService.login(email, password);
    setUser(loggedInUser);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    authService.logout();
  }, []);

  // ── Platform-admin login/logout ─────────────────────────────────────────

  const paLogin = useCallback(async (email: string, password: string) => {
    const paUser = await platformApi.platformAdminLogin(email, password);
    setPlatformUser(paUser);
  }, []);

  const paLogout = useCallback(() => {
    setPlatformUser(null);
    platformApi.platformAdminLogout();
  }, []);

  // ── Impersonation start/stop (write/reset side) ──────────────────────────
  // State-based, never window.location — the React state (and in-memory token)
  // must survive the start/stop flow. The caller navigates client-side.

  const startImpersonation = useCallback(
    async (tenantId: string, reason: platformApi.ImpersonationReason) => {
      const { impersonationToken, session } =
        await platformApi.startImpersonation(tenantId, reason);

      // Token under the tenant key — tenant-admin requests now act as the
      // tenant, and checkImpersonation() picks up the act claim.
      localStorage.setItem("access_token", impersonationToken);

      // Synthetic tenant user so ProtectedRoute (isAuthenticated = !!user)
      // lets /dashboard render under the impersonation token.
      const impUser = impersonationUserFromToken(impersonationToken);
      if (impUser) {
        localStorage.setItem("user", JSON.stringify(impUser));
        setUser(impUser);
      }

      // tenantName is not a JWT claim — persist it for the banner display only.
      localStorage.setItem(
        PA_IMPERSONATION_KEY,
        JSON.stringify({ tenantName: session.tenantName }),
      );
      setImpersonationTenantName(session.tenantName);
    },
    [],
  );

  const stopImpersonation = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    const sessionId = token ? impersonationSessionIdFromToken(token) : null;
    if (sessionId) {
      await platformApi.stopImpersonation(sessionId);
    }

    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem(PA_IMPERSONATION_KEY);
    setUser(null);
    setImpersonationTenantName(null);
  }, []);

  // ── Event listeners ─────────────────────────────────────────────────────

  useEffect(() => {
    const handleAuthExpired = () => setUser(null);
    window.addEventListener("auth:expired", handleAuthExpired);
    return () => window.removeEventListener("auth:expired", handleAuthExpired);
  }, []);

  useEffect(() => {
    const handlePaExpired = () => setPlatformUser(null);
    window.addEventListener("platform-auth:expired", handlePaExpired);
    return () =>
      window.removeEventListener("platform-auth:expired", handlePaExpired);
  }, []);

  // ── Provider ────────────────────────────────────────────────────────────

  return createElement(
    AuthContext.Provider,
    {
      value: {
        user,
        isAuthenticated: !!user,
        isPlatformAdmin,
        isImpersonating,
        impersonationTenantName,
        login,
        logout,
        setAuthUser: setUser,
        platformAdminLogin: paLogin,
        platformAdminLogout: paLogout,
        startImpersonation,
        stopImpersonation,
      },
    },
    children,
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth muss innerhalb von AuthProvider verwendet werden");
  }
  return context;
}
